import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Shield,
  ClipboardCheck,
  Thermometer,
  AlertTriangle,
  FileCheck,
  BookOpen,
  List,
  CheckCircle2,
  XCircle,
  Scale,
  Search,
  FileText,
  Users,
  Bug,
  Snowflake,
  Flame,
  ShoppingCart,
  Sparkles,
} from 'lucide-react';

/* ─── FAQs ───────────────────────────────────────────── */

const faqs = [
  {
    q: '¿Es obligatorio tener un plan APPCC en mi restaurante?',
    a: 'Si. Desde 2004, el Reglamento (CE) 852/2004 obliga a todos los operadores de empresa alimentaria de la UE a implantar un sistema de autocontrol basado en los principios del APPCC. En España, el Real Decreto 640/2006 refuerza esta obligacion. La unica excepcion es la produccion primaria (agricultura, ganaderia), pero todo establecimiento que manipule, transforme o sirva alimentos — incluidos restaurantes, bares, cafeterias y catering — debe tener su plan APPCC documentado y actualizado.',
  },
  {
    q: '¿Quien puede elaborar el plan APPCC de un restaurante?',
    a: 'El plan APPCC puede ser elaborado por el propio responsable del establecimiento si tiene formacion en seguridad alimentaria, o por una consultoria externa especializada. Lo importante es que el equipo APPCC incluya personas con conocimiento practico de las operaciones del restaurante y formacion en los principios del sistema. Muchas comunidades autonomas ofrecen guias sectoriales que facilitan la implantacion.',
  },
  {
    q: '¿Cada cuanto hay que revisar el plan APPCC?',
    a: 'El plan APPCC debe revisarse al menos una vez al ano, pero tambien siempre que haya cambios significativos: nuevo menu, cambio de proveedores, reforma de la cocina, nuevo equipamiento, cambio de procesos de elaboracion o cualquier incidencia que afecte a la seguridad alimentaria. La revision debe quedar documentada.',
  },
  {
    q: '¿Que registros del APPCC debo guardar y durante cuanto tiempo?',
    a: 'Debes conservar los registros de temperaturas (camaras, coccion, servicio), limpieza y desinfeccion, control de proveedores, trazabilidad de materias primas, formacion del personal y acciones correctoras. La legislacion espanola exige conservar estos registros durante un minimo de 2 anos, aunque algunas comunidades autonomas pueden exigir hasta 5 anos.',
  },
  {
    q: '¿Que pasa si llega una inspeccion y no tengo APPCC?',
    a: 'La ausencia total de un plan APPCC se considera una infraccion grave segun la Ley 17/2011 de seguridad alimentaria, con multas de 5.001 a 20.000 euros. Si ademas se detectan riesgos para la salud publica, la sancion puede calificarse como muy grave (hasta 600.000 euros) e incluir el cierre temporal del establecimiento.',
  },
  {
    q: '¿El APPCC sustituye al registro sanitario?',
    a: 'No. El APPCC es un sistema de autocontrol interno del establecimiento, mientras que el Registro General Sanitario de Empresas Alimentarias (RGSEAA) es un tramite administrativo de inscripcion ante la autoridad competente. Ambos son obligatorios y complementarios.',
  },
  {
    q: '¿Necesito el APPCC para obtener la licencia de apertura?',
    a: 'En la mayoria de comunidades autonomas, si. Para obtener la licencia de actividad de un establecimiento de hosteleria se exige tener implantado un sistema de autocontrol basado en APPCC. Los inspectores pueden solicitarlo en la primera visita o durante inspecciones rutinarias.',
  },
  {
    q: '¿Como afecta el APPCC al control de costes del restaurante?',
    a: 'Un buen sistema APPCC reduce las perdidas por producto en mal estado, mejora la rotacion de stock (FIFO/FEFO), minimiza las mermas por temperaturas incorrectas y evita sanciones economicas. Con PLEKO puedes registrar proveedores, controlar trazabilidad de ingredientes y mantener tus escandallos actualizados, lo que complementa tu plan APPCC con datos de costes en tiempo real.',
  },
];

/* ─── 7 Principles ───────────────────────────────────── */

const principles = [
  { num: 1, title: 'Analisis de peligros', desc: 'Identificar todos los peligros biologicos, quimicos y fisicos en cada etapa del proceso de elaboracion de alimentos.' },
  { num: 2, title: 'Determinar los Puntos Criticos de Control (PCC)', desc: 'Identificar las etapas donde el control es esencial para prevenir o eliminar un peligro (recepcion, coccion, enfriamiento, etc.).' },
  { num: 3, title: 'Establecer limites criticos', desc: 'Definir valores medibles que separan lo aceptable de lo inaceptable en cada PCC (temperaturas, tiempos, pH, etc.).' },
  { num: 4, title: 'Establecer un sistema de vigilancia', desc: 'Definir que se mide, como, cuando y quien lo hace para monitorizar cada PCC de forma continua o periodica.' },
  { num: 5, title: 'Establecer medidas correctoras', desc: 'Definir las acciones a tomar cuando la vigilancia indica que un PCC esta fuera de los limites criticos.' },
  { num: 6, title: 'Establecer procedimientos de verificacion', desc: 'Confirmar que el sistema APPCC funciona correctamente mediante auditorias, analisis y revision de registros.' },
  { num: 7, title: 'Establecer un sistema de documentacion y registro', desc: 'Mantener toda la documentacion del plan APPCC y los registros que demuestren su aplicacion diaria.' },
];

/* ─── 12 steps (Codex) ───────────────────────────────── */

const codexSteps = [
  { num: 1, step: 'Constituir el equipo APPCC', type: 'previo' },
  { num: 2, step: 'Describir el producto', type: 'previo' },
  { num: 3, step: 'Determinar el uso previsto', type: 'previo' },
  { num: 4, step: 'Elaborar el diagrama de flujo', type: 'previo' },
  { num: 5, step: 'Confirmar in situ el diagrama', type: 'previo' },
  { num: 6, step: 'Analisis de peligros (Principio 1)', type: 'principio' },
  { num: 7, step: 'Determinar los PCC (Principio 2)', type: 'principio' },
  { num: 8, step: 'Establecer limites criticos (Principio 3)', type: 'principio' },
  { num: 9, step: 'Sistema de vigilancia (Principio 4)', type: 'principio' },
  { num: 10, step: 'Medidas correctoras (Principio 5)', type: 'principio' },
  { num: 11, step: 'Verificacion (Principio 6)', type: 'principio' },
  { num: 12, step: 'Documentacion y registro (Principio 7)', type: 'principio' },
];

/* ─── Critical temperatures ──────────────────────────── */

const temperatures = [
  { etapa: 'Recepcion de refrigerados', limite: '≤ 5°C', accion: 'Rechazar si > 8°C. Registrar temperatura y proveedor.' },
  { etapa: 'Recepcion de congelados', limite: '≤ -18°C', accion: 'Rechazar si > -15°C. Verificar ausencia de cristales grandes.' },
  { etapa: 'Almacenamiento en frio', limite: '0°C a 5°C', accion: 'Registrar 2 veces/dia. Reparar camara si supera 7°C.' },
  { etapa: 'Almacenamiento en congelacion', limite: '≤ -18°C', accion: 'Registrar diariamente. No recongelar producto descongelado.' },
  { etapa: 'Descongelacion', limite: '< 5°C (en camara)', accion: 'Nunca a temperatura ambiente. Consumir en 24h tras descongelar.' },
  { etapa: 'Coccion', limite: '≥ 75°C en el centro', accion: 'Medir con termometro-sonda. Aves y cerdo: minimo 75°C.' },
  { etapa: 'Enfriamiento rapido', limite: 'De 65°C a 10°C en < 2h', accion: 'Usar abatidor. Si no hay, bano maria inverso + porciones pequenas.' },
  { etapa: 'Servicio caliente', limite: '≥ 65°C', accion: 'Mantener en bano maria o vitrina caliente. Maximo 2h fuera.' },
  { etapa: 'Servicio frio', limite: '≤ 8°C', accion: 'Vitrina refrigerada. Maximo 2h sin refrigeracion.' },
  { etapa: 'Recalentamiento', limite: '≥ 75°C en el centro', accion: 'Recalentar una sola vez. Desechar si no alcanza temperatura.' },
];

/* ─── Common mistakes ────────────────────────────────── */

const mistakes = [
  { error: 'Tener un plan APPCC generico descargado de internet', fix: 'El plan debe ser especifico para tu restaurante, tus procesos, tu cocina y tu carta.' },
  { error: 'No registrar temperaturas a diario', fix: 'Los registros deben completarse cada dia, no rellenarse antes de la inspeccion.' },
  { error: 'No actualizar el plan cuando cambia la carta', fix: 'Cada plato nuevo puede introducir nuevos peligros y PCC que deben evaluarse.' },
  { error: 'No formar al personal', fix: 'Todo el equipo de cocina debe conocer los PCC, los limites criticos y las acciones correctoras.' },
  { error: 'Confundir limpieza con desinfeccion', fix: 'Limpiar elimina suciedad visible; desinfectar elimina microorganismos. Son dos pasos distintos.' },
  { error: 'No verificar la temperatura de recepcion de mercancias', fix: 'Cada entrega debe verificarse con termometro. Si supera el limite, se rechaza.' },
  { error: 'No tener acciones correctoras documentadas', fix: 'Si un PCC se sale de control, el personal debe saber que hacer sin improvisar.' },
  { error: 'Guardar registros solo en papel sin copia', fix: 'Los registros deben conservarse minimo 2 anos. Un respaldo digital evita perdidas.' },
];

/* ─── Table of Contents ──────────────────────────────── */

const toc = [
  { id: 'que-es', label: 'Que es el APPCC y por que es obligatorio' },
  { id: 'principios', label: 'Los 7 principios del sistema APPCC' },
  { id: 'codex', label: 'Los 12 pasos del Codex Alimentarius' },
  { id: 'pcc-restaurante', label: 'Puntos criticos de control en un restaurante' },
  { id: 'temperaturas', label: 'Tabla de temperaturas criticas' },
  { id: 'crear-plan', label: 'Como crear tu plan APPCC paso a paso' },
  { id: 'registros', label: 'Registros obligatorios del APPCC' },
  { id: 'sanciones', label: 'Sanciones por no tener APPCC' },
  { id: 'trazabilidad', label: 'APPCC y trazabilidad alimentaria' },
  { id: 'alergenos', label: 'APPCC y control de alergenos' },
  { id: 'pleko', label: 'Como PLEKO ayuda con el control de proveedores y trazabilidad' },
  { id: 'errores', label: 'Errores comunes en la implantacion del APPCC' },
  { id: 'faqs', label: 'Preguntas frecuentes' },
];

/* ─── Schema.org JSON-LD ─────────────────────────────── */

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Plan APPCC en Restaurantes',
      item: 'https://pleko.app/appcc-restaurante',
    },
  ],
};

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Plan APPCC en Restaurantes: Guia Completa para Cumplir la Normativa Obligatoria',
  description: 'Guia completa del sistema APPCC obligatorio para restaurantes en España. Los 7 principios, puntos criticos de control, temperaturas, registros obligatorios y sanciones.',
  author: { '@type': 'Organization', name: 'PLEKO' },
  publisher: { '@type': 'Organization', name: 'PLEKO', url: 'https://pleko.app' },
  datePublished: '2026-06-18',
  dateModified: '2026-06-18',
  mainEntityOfPage: 'https://pleko.app/appcc-restaurante',
};

/* ─── Component ──────────────────────────────────────── */

export default function AppccRestaurantePage() {
  return (
    <>
      <Helmet>
        <title>Plan APPCC en Restaurantes: Guia Completa Normativa Obligatoria 2026 | PLEKO</title>
        <meta
          name="description"
          content="Guia completa del sistema APPCC obligatorio para restaurantes en España 2026. Los 7 principios, puntos criticos de control, temperaturas, registros, sanciones de hasta 600.000 euros y como cumplir la normativa."
        />
        <meta
          name="keywords"
          content="APPCC restaurante, plan APPCC restaurante, APPCC obligatorio, sistema APPCC hosteleria, puntos criticos control restaurante, APPCC cocina, HACCP restaurante, autocontrol restaurante, seguridad alimentaria restaurante, plan autocontrol hosteleria"
        />
        <link rel="canonical" href="https://pleko.app/appcc-restaurante" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/appcc-restaurante" />
        <meta
          property="og:title"
          content="Plan APPCC en Restaurantes: Guia Completa Normativa Obligatoria 2026"
        />
        <meta
          property="og:description"
          content="Guia completa del APPCC obligatorio para restaurantes. 7 principios, temperaturas criticas, registros y sanciones de hasta 600.000 euros."
        />
        <meta property="og:image" content="https://pleko.app/og-image.png" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Plan APPCC en Restaurantes: Guia Completa 2026 | PLEKO"
        />
        <meta
          name="twitter:description"
          content="Guia del APPCC obligatorio para hosteleria. 7 principios, PCC, temperaturas criticas y sanciones de hasta 600.000 euros."
        />
        <meta name="twitter:image" content="https://pleko.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* ═══════════ HERO ═══════════ */}
        <section className="bg-gradient-to-b from-red-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                Guia APPCC obligatoria para hosteleria — 2026
              </span>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
                Plan APPCC en Restaurantes:{' '}
                <span className="text-red-600">Guia Completa para Cumplir la Normativa Obligatoria</span>
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                El APPCC es obligatorio para todos los restaurantes en España desde 2004. Las sanciones por no tenerlo pueden alcanzar los <strong>600.000 euros</strong>. En esta guia te explicamos los 7 principios, los puntos criticos de control, las temperaturas clave y como implantar tu plan paso a paso.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#que-es"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors shadow-lg shadow-red-200"
                >
                  Leer la guia completa
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-slate-200 text-slate-700 font-semibold rounded-xl hover:border-slate-300 transition-colors"
                >
                  <ClipboardCheck className="w-4 h-4" />
                  Probar PLEKO gratis
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ KEY STATS ═══════════ */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 mb-8">
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { stat: '600.000 euros', label: 'Multa maxima por infraccion muy grave', color: 'red' },
              { stat: '7 principios', label: 'Base del sistema APPCC (Codex Alimentarius)', color: 'amber' },
              { stat: '75°C', label: 'Temperatura minima de coccion en el centro', color: 'orange' },
              { stat: '2 anos', label: 'Tiempo minimo de conservacion de registros', color: 'slate' },
            ].map(({ stat, label }) => (
              <div key={stat} className="bg-white border border-slate-200 rounded-xl p-4 text-center shadow-sm">
                <p className="text-2xl font-bold text-red-600">{stat}</p>
                <p className="text-xs text-slate-500 mt-1">{label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ═══════════ CONTENT ═══════════ */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Table of Contents */}
          <nav className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-5 h-5 text-red-600" />
              <h2 className="text-lg font-bold text-slate-900">Contenido de esta guia</h2>
            </div>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {toc.map((item, i) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-sm text-red-600 hover:text-red-800 hover:underline">
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="prose prose-lg max-w-none">
            {/* ═══════════ SECTION 1: QUE ES EL APPCC ═══════════ */}
            <h2 id="que-es" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Que es el APPCC y por que es obligatorio en restaurantes
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El <strong>APPCC (Analisis de Peligros y Puntos Criticos de Control)</strong> — conocido internacionalmente como HACCP — es un sistema preventivo de seguridad alimentaria que identifica, evalua y controla los peligros que pueden afectar a la inocuidad de los alimentos en cada etapa de su produccion y manipulacion.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              A diferencia de los controles reactivos (analizar el producto final), el APPCC se centra en <strong>prevenir los problemas antes de que ocurran</strong>. En lugar de esperar a que un cliente enferme, el sistema identifica los puntos donde puede aparecer un peligro y establece controles sistematicos para evitarlo.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
              <div className="flex items-start gap-3">
                <Scale className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">Base legal en España</p>
                  <ul className="text-amber-700 text-sm mt-2 space-y-1">
                    <li><strong>Reglamento (CE) 852/2004</strong> — Obliga a todos los operadores de empresa alimentaria de la UE a implantar un sistema basado en los principios APPCC.</li>
                    <li><strong>Real Decreto 640/2006</strong> — Transpone la normativa europea al ordenamiento juridico español y establece los requisitos de higiene para la produccion y comercializacion de alimentos.</li>
                    <li><strong>Ley 17/2011 de Seguridad Alimentaria y Nutricion</strong> — Define el regimen sancionador con multas de hasta 600.000 euros por infracciones muy graves.</li>
                  </ul>
                </div>
              </div>
            </div>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              En la practica, <strong>todo restaurante, bar, cafeteria, catering, comedor escolar u hospital</strong> que manipule o sirva alimentos debe tener un plan APPCC documentado, actualizado y con registros que demuestren su aplicacion diaria. No tenerlo es una infraccion sancionable que puede detectarse en cualquier inspeccion de sanidad.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El APPCC no es un documento que se redacta una vez y se guarda en un cajon. Es un <strong>sistema vivo</strong> que debe adaptarse cada vez que cambia tu carta, tus proveedores, tu equipamiento o tus procesos de cocina. Los inspectores no solo comprueban que el plan existe — verifican que los registros estan al dia y que el personal conoce los procedimientos.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              A pesar de ser obligatorio desde hace mas de 20 anos, muchos restaurantes en España siguen sin tener un APPCC correctamente implantado. Las causas mas habituales son desconocimiento de la normativa, falta de formacion y la percepcion erronea de que "es demasiado complejo para un restaurante pequeno". Esta guia demuestra que no lo es.
            </p>

            {/* ═══════════ SECTION 2: 7 PRINCIPIOS ═══════════ */}
            <h2 id="principios" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Los 7 principios del sistema APPCC
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El sistema APPCC se fundamenta en 7 principios establecidos por el <strong>Codex Alimentarius</strong> (comision conjunta FAO/OMS). Estos principios, reconocidos internacionalmente, son la columna vertebral de cualquier plan de autocontrol en hosteleria. Todos los paises de la UE los aplican de forma identica, lo que facilita la armonizacion de la seguridad alimentaria en toda Europa.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Los 7 principios se aplican de forma secuencial: primero se identifican los peligros, despues se determinan los puntos criticos, se establecen los limites y asi sucesivamente. Cada principio se apoya en el anterior, formando un sistema coherente e integral:
            </p>
            <div className="space-y-4 mb-10">
              {principles.map(({ num, title, desc }) => (
                <div key={num} className="flex gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
                  <span className="w-9 h-9 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm">{num}</span>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1">Principio {num}: {title}</h3>
                    <p className="text-slate-600 text-sm">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* ═══════════ SECTION 3: 12 PASOS CODEX ═══════════ */}
            <h2 id="codex" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Los 12 pasos del Codex Alimentarius para implantar el APPCC
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El Codex Alimentarius establece una secuencia de 12 pasos que incluye 5 etapas previas (preparacion) mas los 7 principios del APPCC. Esta es la metodologia completa que debe seguir cualquier establecimiento:
            </p>
            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-center font-medium w-12">Paso</th>
                    <th className="p-3 text-left font-medium">Descripcion</th>
                    <th className="p-3 text-center font-medium w-28">Tipo</th>
                  </tr>
                </thead>
                <tbody>
                  {codexSteps.map(({ num, step, type }, i) => (
                    <tr key={num} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 text-center font-bold text-red-600">{num}</td>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{step}</td>
                      <td className="p-3 border-b border-slate-100 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${type === 'previo' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>
                          {type === 'previo' ? 'Paso previo' : 'Principio APPCC'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ═══════════ SECTION 4: PCC EN RESTAURANTE ═══════════ */}
            <h2 id="pcc-restaurante" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Puntos criticos de control en un restaurante
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              En un restaurante tipico, los principales Puntos Criticos de Control (PCC) se concentran en las etapas donde un fallo puede provocar una toxiinfeccion alimentaria. Un PCC no es cualquier punto de control — es una etapa donde la perdida de control supone un riesgo directo para la salud del consumidor y donde no existe una etapa posterior que pueda corregir el fallo.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Para determinar si una etapa es un PCC, se utiliza el <strong>arbol de decisiones del Codex Alimentarius</strong>: una serie de preguntas logicas que guian la clasificacion. No todas las etapas son PCC — muchas son simples puntos de control (PC) donde las medidas preventivas son suficientes. Estos son los PCC mas habituales en un restaurante:
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-10">
              {[
                { icon: ShoppingCart, title: 'Recepcion de mercancias', desc: 'Verificar temperaturas, fechas de caducidad, estado del embalaje y documentacion del proveedor. Es la primera barrera de control.' },
                { icon: Snowflake, title: 'Almacenamiento en frio', desc: 'Mantener la cadena de frio: refrigerados a 0-5°C, congelados a -18°C o menos. Separar productos crudos y cocinados.' },
                { icon: Thermometer, title: 'Descongelacion', desc: 'Siempre en camara frigorifica (< 5°C), nunca a temperatura ambiente. Consumir en 24 horas y no recongelar.' },
                { icon: Flame, title: 'Coccion', desc: 'Alcanzar minimo 75°C en el centro del producto. Medir con termometro-sonda, especialmente en aves, cerdo y elaboraciones con huevo.' },
                { icon: Thermometer, title: 'Enfriamiento rapido', desc: 'De 65°C a 10°C en menos de 2 horas. Usar abatidor de temperatura o tecnicas de enfriamiento rapido.' },
                { icon: ClipboardCheck, title: 'Servicio (caliente y frio)', desc: 'Caliente: mantener a 65°C o mas. Frio: mantener a 8°C o menos. Maximo 2 horas fuera de temperatura.' },
                { icon: Sparkles, title: 'Limpieza y desinfeccion', desc: 'Plan documentado de limpieza con frecuencias, productos y responsables. Verificar con controles visuales y microbiologicos.' },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-5 h-5 text-red-500" />
                    <h3 className="font-semibold text-slate-800 text-sm">{title}</h3>
                  </div>
                  <p className="text-slate-600 text-sm">{desc}</p>
                </div>
              ))}
            </div>

            {/* ═══════════ SECTION 5: TEMPERATURAS ═══════════ */}
            <h2 id="temperaturas" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Tabla de temperaturas criticas del APPCC en restaurantes
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Las temperaturas son el parametro mas importante del APPCC en un restaurante. La mayoria de toxiinfecciones alimentarias se producen por una ruptura en la cadena de frio o por una coccion insuficiente. Controlar y registrar las temperaturas a diario es el nucleo del sistema de vigilancia de cualquier restaurante.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Invierte en <strong>termometros-sonda calibrados</strong> (verificalos al menos una vez al ano) y en termometros de camara con registro continuo. Esta tabla resume los limites criticos que no puedes superar:
            </p>
            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-red-700 text-white">
                    <th className="p-3 text-left font-medium">Etapa</th>
                    <th className="p-3 text-center font-medium">Limite critico</th>
                    <th className="p-3 text-left font-medium">Accion correctora</th>
                  </tr>
                </thead>
                <tbody>
                  {temperatures.map(({ etapa, limite, accion }, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-red-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{etapa}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-bold text-red-600">{limite}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600 text-sm">{accion}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ═══════════ SECTION 6: CREAR PLAN PASO A PASO ═══════════ */}
            <h2 id="crear-plan" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Como crear tu plan APPCC paso a paso
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Implantar un plan APPCC no tiene por que ser complicado ni caro. Muchos restaurantes pequenos creen que necesitan contratar una consultoria externa, pero con la formacion adecuada puedes elaborar tu propio plan. Las comunidades autonomas publican guias sectoriales gratuitas que sirven como base. Sigue estos 7 pasos practicos adaptados a la realidad de un restaurante:
            </p>
            <div className="space-y-4 mb-10">
              {[
                { num: '1', title: 'Forma tu equipo APPCC', desc: 'Designa un responsable (normalmente el jefe de cocina o el gerente) y asegurate de que tenga formacion en seguridad alimentaria. El equipo debe conocer las operaciones del restaurante a fondo.' },
                { num: '2', title: 'Describe tus productos y procesos', desc: 'Documenta todos los platos de tu carta, las materias primas que usas, los procesos de elaboracion (coccion, fritura, crudo) y como se sirven al cliente. Elabora un diagrama de flujo de tu cocina.' },
                { num: '3', title: 'Identifica los peligros en cada etapa', desc: 'Para cada paso del proceso (recepcion, almacenamiento, preparacion, coccion, emplatado, servicio), identifica los peligros biologicos (bacterias, virus), quimicos (productos de limpieza, alergenos) y fisicos (cristales, huesos, plasticos).' },
                { num: '4', title: 'Determina tus PCC y limites criticos', desc: 'Usando el arbol de decisiones del Codex, identifica en que puntos es imprescindible un control. Define los limites medibles: temperaturas, tiempos, concentraciones de desinfectante, etc.' },
                { num: '5', title: 'Establece el sistema de vigilancia', desc: 'Define que se mide (temperatura, tiempo, aspecto), con que frecuencia (cada entrega, 2 veces al dia, cada lote), quien lo hace y como se registra. Usa hojas de registro impresas o un sistema digital.' },
                { num: '6', title: 'Define acciones correctoras', desc: 'Para cada PCC, documenta que hacer si se supera el limite critico: rechazar la mercancia, desechar el producto, repetir el proceso, aumentar la temperatura, etc. El personal debe saberlo sin tener que consultar al responsable.' },
                { num: '7', title: 'Verifica, documenta y revisa', desc: 'Realiza verificaciones periodicas (revision de registros, calibracion de termometros, analisis microbiologicos). Conserva toda la documentacion minimo 2 anos. Revisa el plan al menos una vez al ano o cuando haya cambios.' },
              ].map(({ num, title, desc }) => (
                <div key={num} className="flex gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
                  <span className="w-9 h-9 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm">{num}</span>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1">{title}</h3>
                    <p className="text-slate-600 text-sm">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* ═══════════ SECTION 7: REGISTROS OBLIGATORIOS ═══════════ */}
            <h2 id="registros" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Registros obligatorios del APPCC
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Los registros son la prueba de que tu plan APPCC se aplica a diario. Sin registros, el plan no tiene validez ante una inspeccion. Un plan APPCC perfecto sin registros diarios es equivalente a no tener plan. Los inspectores lo primero que piden son los registros de los ultimos meses.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La legislacion espanola exige conservar todos los registros durante un <strong>minimo de 2 anos</strong>, aunque es recomendable guardarlos durante 5 anos. Puedes usar formatos en papel (hojas de registro impresas) o sistemas digitales. Estos son los registros obligatorios que debes mantener al dia:
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-10">
              {[
                { icon: Thermometer, title: 'Registro de temperaturas', desc: 'Camaras frigorificas (2 veces/dia), congeladores, coccion, servicio caliente y frio. Incluir fecha, hora, temperatura y firma del responsable.' },
                { icon: Sparkles, title: 'Registro de limpieza y desinfeccion', desc: 'Frecuencia, productos utilizados, zonas limpiadas, responsable. Incluir verificaciones visuales y resultados de controles microbiologicos.' },
                { icon: ShoppingCart, title: 'Control de proveedores', desc: 'Listado de proveedores homologados con numero de RGSEAA, albaranes con temperaturas de recepcion, incidencias y acciones correctoras.' },
                { icon: Search, title: 'Trazabilidad de materias primas', desc: 'Lote, fecha de recepcion, proveedor, fecha de caducidad y destino de cada materia prima. Permite rastrear el origen de cualquier ingrediente.' },
                { icon: Users, title: 'Formacion del personal', desc: 'Registro de cursos realizados, certificados de manipulador de alimentos, formacion interna en APPCC y buenas practicas de higiene.' },
                { icon: FileCheck, title: 'Acciones correctoras', desc: 'Registro de cada desviacion detectada: que PCC se supero, que se hizo, quien lo detecto y resultado de la correccion.' },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-5 h-5 text-red-500" />
                    <h3 className="font-semibold text-slate-800 text-sm">{title}</h3>
                  </div>
                  <p className="text-slate-600 text-sm">{desc}</p>
                </div>
              ))}
            </div>

            {/* ═══════════ SECTION 8: SANCIONES ═══════════ */}
            <h2 id="sanciones" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Sanciones por no tener APPCC en tu restaurante
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La <strong>Ley 17/2011 de Seguridad Alimentaria y Nutricion</strong> establece un regimen sancionador con tres niveles de gravedad. Las sanciones se aplican tanto por la ausencia total del plan APPCC como por deficiencias graves en su aplicacion:
            </p>
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
              <div className="flex gap-3">
                <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-bold text-red-800 mb-3">Regimen sancionador (Ley 17/2011)</p>
                  <div className="grid md:grid-cols-3 gap-3 text-sm">
                    <div className="bg-white rounded-lg p-3">
                      <p className="font-bold text-slate-800">Infraccion leve</p>
                      <p className="text-red-600 font-bold text-lg">Hasta 5.000 euros</p>
                      <p className="text-slate-500 text-xs">Deficiencias menores en registros, formacion incompleta del personal, pequenas irregularidades documentales.</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="font-bold text-slate-800">Infraccion grave</p>
                      <p className="text-red-600 font-bold text-lg">5.001 - 20.000 euros</p>
                      <p className="text-slate-500 text-xs">Ausencia de plan APPCC, falta de registros sistematicos, negligencia en el control de temperaturas, reincidencia.</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="font-bold text-slate-800">Infraccion muy grave</p>
                      <p className="text-red-600 font-bold text-lg">20.001 - 600.000 euros</p>
                      <p className="text-slate-500 text-xs">Riesgo grave para la salud publica, ocultacion deliberada, intoxicaciones alimentarias. Puede incluir cierre temporal.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Ademas de las multas economicas, una infraccion grave puede suponer el <strong>cierre temporal del establecimiento</strong>, la retirada de la licencia de actividad y un dano reputacional irreversible. Las inspecciones de sanidad pueden producirse sin previo aviso en cualquier momento.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Las comunidades autonomas gestionan las inspecciones a traves de sus consejerias de sanidad. La frecuencia varia segun el tipo de establecimiento y el historial de cumplimiento, pero un restaurante puede recibir entre 1 y 3 inspecciones al ano. Durante una inspeccion, el inspector revisara:
            </p>
            <ul className="space-y-2 mb-6">
              {[
                'Existencia y actualizacion del plan APPCC documentado.',
                'Registros de temperaturas, limpieza, proveedores y trazabilidad.',
                'Condiciones higienicas de la cocina, almacenes y zona de servicio.',
                'Formacion del personal (certificados de manipulador, formacion APPCC).',
                'Etiquetado y conservacion de materias primas.',
                'Separacion de productos crudos y cocinados.',
                'Estado de los equipos de frio (camaras, congeladores, vitrinas).',
              ].map((item) => (
                <li key={item} className="flex gap-2 text-slate-700">
                  <Search className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              Para prepararte correctamente, consulta nuestra guia sobre <Link to="/inspeccion-sanidad-restaurante" className="text-red-600 hover:underline">como preparar una inspeccion de sanidad en tu restaurante</Link>.
            </p>

            {/* ═══════════ SECTION 9: TRAZABILIDAD ═══════════ */}
            <h2 id="trazabilidad" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              APPCC y trazabilidad alimentaria
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La trazabilidad es un pilar fundamental del APPCC. El <strong>Reglamento (CE) 178/2002</strong> obliga a todos los operadores alimentarios a poder identificar el origen de cualquier materia prima que utilicen y el destino de los productos que elaboren. En un restaurante, esto significa:
            </p>
            <ul className="space-y-2 mb-6">
              {[
                'Registrar proveedor, lote y fecha de recepcion de cada ingrediente.',
                'Conservar albaranes y facturas de compra con datos de trazabilidad.',
                'Poder rastrear en que platos se ha utilizado un lote concreto.',
                'Actuar rapidamente ante una alerta alimentaria retirando los productos afectados.',
              ].map((item) => (
                <li key={item} className="flex gap-2 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Un sistema de trazabilidad bien implementado no solo cumple la normativa, sino que te protege legalmente ante cualquier incidencia. En caso de una alerta alimentaria (por ejemplo, un lote de carne contaminada), debes poder identificar en menos de 4 horas que productos estan afectados, a quien se los has servido y que cantidad te queda en stock.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La trazabilidad se divide en tres niveles: <strong>trazabilidad hacia atras</strong> (de quien compro cada ingrediente), <strong>trazabilidad interna</strong> (que hago con cada ingrediente en mi cocina) y <strong>trazabilidad hacia adelante</strong> (a quien sirvo el producto final). En un restaurante, la trazabilidad hacia adelante es limitada (no identificas a cada comensal), pero las dos primeras son obligatorias y verificables.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              Consulta nuestra guia completa sobre <Link to="/trazabilidad-alimentaria" className="text-red-600 hover:underline">trazabilidad alimentaria en restaurantes</Link> para un detalle practico de como implementarla.
            </p>

            {/* ═══════════ SECTION 10: ALERGENOS ═══════════ */}
            <h2 id="alergenos" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              APPCC y control de alergenos
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El control de <Link to="/alergenos-restaurante" className="text-red-600 hover:underline">alergenos</Link> es una parte esencial del analisis de peligros quimicos dentro del APPCC. Los alergenos son un peligro que puede causar reacciones alergicas graves (anafilaxia) e incluso la muerte en personas sensibles. El <strong>Reglamento (UE) 1169/2011</strong> y el <strong>Real Decreto 126/2015</strong> obligan a informar sobre los 14 alergenos de declaracion obligatoria en todos los platos servidos.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Dentro de tu plan APPCC, los alergenos deben tratarse como un peligro quimico en el analisis de peligros (Principio 1). Los puntos criticos de control relacionados con alergenos incluyen la recepcion de materias primas, el almacenamiento separado, la preparacion y el servicio al cliente. Tu plan debe incluir:
            </p>
            <ul className="space-y-2 mb-6">
              {[
                'Identificacion de los 14 alergenos de declaracion obligatoria en cada ingrediente.',
                'Procedimientos para evitar la contaminacion cruzada (utensilios separados, orden de elaboracion).',
                'Informacion clara al cliente sobre los alergenos de cada plato (carta, iconos, verbal con cartel).',
                'Formacion especifica del personal sobre alergenos y protocolos de actuacion ante una reaccion alergica.',
              ].map((item) => (
                <li key={item} className="flex gap-2 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              Con PLEKO, puedes asignar alergenos a cada ingrediente y el sistema muestra automaticamente los alergenos de cada receta en tu <Link to="/carta-digital-restaurante" className="text-red-600 hover:underline">carta digital</Link>, cumpliendo la normativa sin trabajo manual adicional.
            </p>

            {/* ═══════════ SECTION 11: PLEKO ═══════════ */}
            <h2 id="pleko" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Como PLEKO ayuda con el control de proveedores y trazabilidad
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Aunque PLEKO no es un software de APPCC especifico (no genera el plan por ti), muchas de sus funcionalidades cubren areas criticas del autocontrol que normalmente se gestionan con papel o Excel. Al digitalizar el control de proveedores, ingredientes y recetas, PLEKO te proporciona una base de datos trazable que complementa tu plan APPCC:
            </p>
            <div className="space-y-3 mb-10">
              {[
                { title: 'Registro de proveedores homologados', desc: 'Gestiona tu listado de proveedores con datos de contacto, historico de compras y trazabilidad de cada ingrediente.' },
                { title: 'OCR de facturas y albaranes', desc: 'Escanea facturas y albaranes con IA para registrar automaticamente precios, lotes y fechas. Un respaldo digital de tu documentacion de proveedores.' },
                { title: 'Trazabilidad de ingredientes', desc: 'Cada ingrediente esta vinculado a su proveedor y se refleja en las recetas. Puedes rastrear que ingredientes componen cada plato.' },
                { title: 'Alergenos automaticos', desc: 'Asigna alergenos a ingredientes una vez y PLEKO los propaga automaticamente a todas las recetas y a la carta digital.' },
                { title: 'Escandallos con control de costes', desc: 'Los escandallos actualizados te ayudan a controlar no solo el coste sino tambien la composicion exacta de cada plato, facilitando la revision del APPCC cuando cambia la carta.' },
                { title: 'Almacen y rotacion de stock', desc: 'El modulo de almacen ayuda a controlar la rotacion FIFO/FEFO y detectar productos proximos a caducar, un requisito clave del APPCC.' },
              ].map(({ title, desc }) => (
                <div key={title} className="flex gap-3 p-4 bg-red-50 rounded-xl border border-red-100">
                  <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{title}</p>
                    <p className="text-slate-600 text-sm">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* ═══════════ SECTION 12: ERRORES COMUNES ═══════════ */}
            <h2 id="errores" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Errores comunes en la implantacion del APPCC
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Despues de anos de inspecciones, los errores que se repiten en los restaurantes espanoles son sorprendentemente similares. La mayoria no son por mala fe, sino por desconocimiento o falta de habito. Estos son los 8 errores que los inspectores detectan con mas frecuencia — evitalos para asegurar que tu plan APPCC sea efectivo y pase cualquier inspeccion:
            </p>
            <div className="space-y-3 mb-10">
              {mistakes.map(({ error, fix }) => (
                <div key={error} className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex gap-2 mb-2">
                    <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="font-semibold text-red-700 text-sm">{error}</p>
                  </div>
                  <div className="flex gap-2 ml-7">
                    <Bug className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <p className="text-slate-600 text-sm">{fix}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ═══════════ DIFERENCIA APPCC vs PRERREQUISITOS ═══════════ */}
          <div className="mt-12 bg-slate-50 border border-slate-200 rounded-xl p-6">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-red-600" /> APPCC vs. Prerrequisitos: no son lo mismo
            </h3>
            <p className="text-slate-600 text-sm mb-3">
              Un error frecuente es confundir el APPCC con los <strong>planes de prerrequisitos</strong> (tambien llamados Planes Generales de Higiene o PGH). Los prerrequisitos son las condiciones basicas de higiene que deben existir antes de implantar el APPCC: plan de limpieza, plan de desinsectacion y desratizacion, plan de formacion, plan de mantenimiento, plan de control de agua y plan de gestion de residuos.
            </p>
            <p className="text-slate-600 text-sm">
              El APPCC se construye <strong>sobre</strong> los prerrequisitos. Si no tienes las condiciones basicas de higiene resueltas (limpieza, control de plagas, agua potable), el APPCC no puede funcionar. Ambos sistemas son obligatorios y complementarios. Los inspectores verifican ambos.
            </p>
          </div>

          {/* ═══════════ RESUMEN ═══════════ */}
          <div className="mt-12 bg-amber-50 border border-amber-200 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <Shield className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-amber-800 mb-2">Resumen: lo imprescindible del APPCC</h3>
                <ul className="space-y-2 text-sm text-amber-700">
                  <li><strong>Es obligatorio:</strong> Todo restaurante en España debe tener un plan APPCC documentado (Reglamento CE 852/2004).</li>
                  <li><strong>7 principios:</strong> Analisis de peligros, PCC, limites criticos, vigilancia, acciones correctoras, verificacion y documentacion.</li>
                  <li><strong>Temperaturas:</strong> Los limites criticos mas importantes son recepcion (&le;5°C), coccion (&ge;75°C centro), servicio caliente (&ge;65°C) y frio (&le;8°C).</li>
                  <li><strong>Registros diarios:</strong> Sin registros de temperaturas, limpieza, proveedores y trazabilidad, el plan APPCC no tiene validez.</li>
                  <li><strong>Sanciones:</strong> De 5.000 a 600.000 euros segun gravedad, mas posible cierre temporal del establecimiento.</li>
                  <li><strong>Revision anual:</strong> El plan debe actualizarse al menos una vez al ano o cuando haya cambios significativos.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* ═══════════ FAQ ═══════════ */}
          <div id="faqs" className="mt-16 scroll-mt-20">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
              Preguntas frecuentes sobre APPCC en restaurantes
            </h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4 flex-shrink-0">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* ═══════════ INTERNAL LINKS ═══════════ */}
          <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-red-600" /> Guias relacionadas
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/alergenos-restaurante', label: 'Alergenos en restaurantes: normativa y como cumplirla' },
                { to: '/desperdicio-alimentario-restaurante', label: 'Desperdicio alimentario: normativa y reduccion' },
                { to: '/control-horario-hosteleria', label: 'Control horario obligatorio en hosteleria' },
                { to: '/trazabilidad-alimentaria', label: 'Trazabilidad alimentaria en restaurantes' },
                { to: '/inspeccion-sanidad-restaurante', label: 'Como preparar una inspeccion de sanidad' },
                { to: '/escandallo', label: 'Que es un escandallo: guia completa' },
                { to: '/control-costes-restaurante', label: 'Control de costes en restaurantes: 12 estrategias' },
                { to: '/software-gestion-restaurante', label: 'Software de gestion para restaurantes' },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="flex items-center gap-2 text-sm text-red-600 hover:text-red-800 hover:underline">
                  <ArrowRight className="w-3 h-3 flex-shrink-0" /> {label}
                </Link>
              ))}
            </div>
          </div>

          {/* ═══════════ FINAL CTA ═══════════ */}
          <div className="mt-16 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-8 text-center text-white">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">
              Controla proveedores, trazabilidad y costes con PLEKO
            </h2>
            <p className="text-red-100 mb-6 max-w-xl mx-auto">
              Registro de proveedores, trazabilidad de ingredientes, alergenos automaticos, escandallos en tiempo real y control de almacen. Todo en una plataforma disenada para hosteleria. 14 dias gratis.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-red-700 font-bold rounded-xl hover:bg-red-50 transition-colors"
            >
              Empezar gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-red-200 text-sm">Sin tarjeta de credito. Configurado en 2 minutos.</p>
          </div>
        </section>
      </div>
    </>
  );
}
